import { types, Instance } from "mobx-state-tree";
import { StringModel } from "../../mobxDataModels/stringModel";
import { Mk8sDraftAutoscalerModel } from "./mk8s.draft.providerAutoscaler";
import { Mk8sDraftNetworkingModel } from "./mk8s.draft.providerNetworking";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import { inputValidations } from "../../mobxDataModels/validations";
import { Mk8sDraftProviderGCPAMIModel, Mk8sDraftProviderGCPAMIReadOnlyModel } from "./mk8s.draft.provider.gcp.ami";
import {
  Mk8sDraftProviderGCPNodePoolsModel,
  Mk8sDraftProviderGCPNodePoolsReadonlyModel,
} from "./mk8s.draft.provider.gcp.nodePool";
import { ngParseLink } from "../../utils/linkParser/linkParser";

export const Mk8sDraftProviderGCPModel = types
  .model({
    _projectId: types.optional(types.string, ""),
    projectId: types.optional(StringModel, () =>
      StringModel.create({
        label: "GCP Project ID",
        isRequired: true,
      }),
    ),
    _region: types.optional(types.string, ""),
    region: types.optional(StringModel, () =>
      StringModel.create({
        label: "Region",
        isRequired: true,
      }),
    ),
    _labels: types.optional(types.frozen(), {}),
    labels: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    _tags: types.optional(types.frozen(), []),
    tags: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    _metadata: types.optional(types.frozen(), {}),
    metadata: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    _network: types.optional(types.string, ""),
    network: types.optional(StringModel, () => StringModel.create({ label: "Network", isRequired: true })),
    _saKeyLink: types.optional(types.string, ""),
    saKeyName: types.optional(types.string, ""),
    _image: types.optional(Mk8sDraftProviderGCPAMIReadOnlyModel, () => Mk8sDraftProviderGCPAMIReadOnlyModel.create()),
    image: types.optional(Mk8sDraftProviderGCPAMIModel, () => Mk8sDraftProviderGCPAMIModel.create()),
    _nodePools: types.array(Mk8sDraftProviderGCPNodePoolsReadonlyModel),
    nodePools: types.array(Mk8sDraftProviderGCPNodePoolsModel),
    networking: types.optional(Mk8sDraftNetworkingModel, () => Mk8sDraftNetworkingModel.create()),
    _preInstallScript: types.optional(types.string, ""),
    preInstallScript: types.optional(StringModel, () => StringModel.create({ label: "Pre Install Script" })),
    autoscaler: types.optional(Mk8sDraftAutoscalerModel, () => Mk8sDraftAutoscalerModel.create()),
    removed: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get _saKeyName() {
      const { name: saKeyName } = ngParseLink(self._saKeyLink, { kind: "secret" });
      return saKeyName;
    },
    get saKeyLink() {
      const { absolute } = ngParseLink(self.saKeyName, { kind: "secret" });
      return absolute;
    },
    get labelsAsObject() {
      let res: any = {};
      for (let item of self.labels.items) {
        res[item.firstValue] = item.secondValue;
      }
      return res;
    },
    get metadataAsObject() {
      let res: any = {};
      for (let item of self.metadata.items) {
        res[item.firstValue] = item.secondValue;
      }
      return res;
    },
    get tagsAsArray() {
      return self.tags.items.map((i) => i.firstValue);
    },
  }))
  .actions((self) => ({
    reset() {
      self.projectId.setInitialValue(self._projectId);
      self.region.setInitialValue(self._region);
      self.labels = ListOfItemsModel.create({
        _items: Object.entries(self._labels).map((entry) => ({
          firstValue: entry[0],
          secondValue: entry[1] as string,
        })),
      });
      self.tags = ListOfItemsModel.create({
        _items: self._tags.map((tag) => ({
          firstValue: tag,
        })),
      });
      self.metadata = ListOfItemsModel.create({
        _items: Object.entries(self._metadata).map((entry) => ({
          firstValue: entry[0],
          secondValue: entry[1] as string,
        })),
      });
      self.network.setInitialValue(self._network);
      self.saKeyName = self._saKeyName;

      let imageType: string = "recommended";
      if (self._image.recommended.length > 0) {
        imageType = "recommended";
      } else if (self._image.exact.length > 0) {
        imageType = "exact";
      } else if (self._image.familyProject.length > 0 && self._image.familyFamily.length > 0) {
        imageType = "family";
      }

      self.image = Mk8sDraftProviderGCPAMIModel.create({
        type: imageType,
        recommended: self._image.recommended,
        familyProject: self._image.familyProject,
        familyFamily: self._image.familyFamily,
        exact: self._image.exact,
      });

      self.nodePools.clear();
      for (let index in self._nodePools) {
        const _nodePool = self._nodePools[index];

        self.nodePools.push(
          Mk8sDraftProviderGCPNodePoolsModel.create({
            index: Number(index),
            _pool: JSON.parse(JSON.stringify(_nodePool)),
          }),
        );
      }
      self.networking.reset();
      self.preInstallScript.setInitialValue(self._preInstallScript);
      self.autoscaler.reset();
      self.removed = false;
    },
    confirm() {
      self.projectId.confirm();
      self._projectId = self.projectId.value;
      self.region.confirm();
      self._region = self.region.value;
      self._labels = self.labelsAsObject;
      self.labels.confirm();
      self._tags = self.tagsAsArray;
      self.tags.confirm();
      self._metadata = self.metadataAsObject;
      self.metadata.confirm();
      self.network.confirm();
      self._network = self.network.value;
      self._saKeyLink = self.saKeyLink;
      self._image = {
        type: self.image.type,
        exact: self.image.exact,
        familyProject: self.image.familyProject,
        familyFamily: self.image.familyFamily,
        recommended: self.image.recommended,
      };
      self._nodePools.clear();
      for (let nodePool of self.nodePools) {
        nodePool.confirm();
        self._nodePools.push(
          Mk8sDraftProviderGCPNodePoolsReadonlyModel.create({
            name: nodePool.name.value,
            labels: Object.entries(nodePool.asObject.labels).map(([key, value]) => ({ key: key, value: value })) as any,
            taints: nodePool.asObject.taints,
            machineType: nodePool.machineType.value,
            assignPublicIP: nodePool.assignPublicIP,
            preemptible: nodePool.preemptible,
            zone: nodePool.zone.value,
            overrideImage: {
              type: nodePool.overrideImage.type,
              exact: nodePool.overrideImage.exact,
              familyProject: nodePool.overrideImage.familyProject,
              familyFamily: nodePool.overrideImage.familyFamily,
              recommended: nodePool.overrideImage.recommended,
            },
            bootDiskSize: Number(nodePool.bootDiskSize.value),
            minSize: String(nodePool.minSize.value).length > 0 ? Number(nodePool.minSize.value) : undefined,
            maxSize: String(nodePool.maxSize.value).length > 0 ? Number(nodePool.maxSize.value) : undefined,
            subnet: nodePool.subnet.value,
            localPersistentDisks: nodePool.localPersistentDisks.value,
          }),
        );
      }
      self.networking.confirm();
      self._preInstallScript = self.preInstallScript.value;
      self.preInstallScript.confirm();
      self.autoscaler.confirm();
      self.removed = false;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    setSaKeyName(value: string) {
      self.saKeyName = value;
    },
    addNodePool() {
      let index = 0;
      if (self.nodePools.length > 0) {
        index = self.nodePools[self.nodePools.length - 1].index + 1;
      }
      self.nodePools.push(
        Mk8sDraftProviderGCPNodePoolsModel.create({
          index,
          status: "added",
        }),
      );
    },
    removeNodePoolAt(index: number) {
      const node = self.nodePools.find((np) => np.index === index);
      if (!node) {
        return;
      }
      if (node.status === "default") {
        self.removed = true;
      }
      self.nodePools.remove(node);
    },
  }))
  .views((self) => ({
    isNodePoolNameValid(index: number) {
      let res = true;
      const node = self.nodePools.find((np) => np.index === index)!;
      const pools = [...self.nodePools.filter((np) => np.index !== index).slice()];
      if (pools.some((np) => np.name.value === node.name.value)) {
        res = false;
      }
      return res;
    },
    get anyNodePoolNameConflicts() {
      for (const nodePool of self.nodePools) {
        if (
          [...self.nodePools.filter((np) => np.index !== nodePool.index).slice()].some(
            (np) => np.name.value === nodePool.name.value,
          )
        ) {
          return true;
        }
      }
      return false;
    },
  }))
  .views((self) => ({
    get isLabelsValid() {
      let res = true;
      if (self.labels.items.length > 10) res = false;
      for (const item of self.labels.items) {
        for (const validate of inputValidations.mk8sGcpLabelsKey) {
          const validationResKey = validate("", item.firstValue);
          const validationResValue = item.secondValue ? validate("", item.secondValue) : true;
          if (validationResKey !== true || validationResValue !== true) {
            res = false;
            break;
          }
        }
        for (const validate of inputValidations.mk8sGcpLabelsValue) {
          const validationResKey = validate("", item.secondValue);
          const validationResValue = item.secondValue ? validate("", item.secondValue) : true;
          if (validationResKey !== true || validationResValue !== true) {
            res = false;
            break;
          }
        }
      }
      return res;
    },
    get isTagsValid() {
      let res = true;
      if (self.tags.items.length > 10) res = false;
      for (const item of self.tags.items) {
        for (const validate of inputValidations.mk8sGcpTag) {
          const validationResKey = validate("", item.firstValue);
          const validationResValue = item.secondValue ? validate("", item.secondValue) : true;
          if (validationResKey !== true || validationResValue !== true) {
            res = false;
            break;
          }
        }
      }
      return res;
    },
    get isMetadataValid() {
      let res = true;
      if (self.metadata.items.length > 10) res = false;
      for (const item of self.metadata.items) {
        for (const validate of inputValidations.mk8sGcpMetadataKey) {
          const validationResKey = validate("", item.firstValue);
          const validationResValue = item.secondValue ? validate("", item.secondValue) : true;
          if (validationResKey !== true || validationResValue !== true) {
            res = false;
            break;
          }
        }
      }
      return res;
    },
  }))
  .views((self) => ({
    get isDirty() {
      let res = false;
      if (self._nodePools.length !== self.nodePools.length) res = true;
      if (self.removed) res = true;
      if (self.nodePools.some((n) => n.isDirty)) res = true;
      if (self.projectId.isDirty) res = true;
      if (self.region.isDirty) res = true;
      if (self.network.isDirty) res = true;
      if (self.saKeyName !== self._saKeyName) res = true;
      if (self.image.type !== self._image.type) {
        res = true;
      }
      if (self.image.type === "recommended") {
        if (self.image.recommended !== self._image.recommended) res = true;
      }
      if (self.image.type === "exact") {
        if (self.image.exact !== self._image.exact) res = true;
      }
      if (self.image.type === "family") {
        if (self.image.familyProject !== self._image.familyProject) res = true;
        if (self.image.familyFamily !== self._image.familyFamily) res = true;
      }
      if (self.labels.isDirty) res = true;
      if (self.tags.isDirty) res = true;
      if (self.metadata.isDirty) res = true;
      if (self.preInstallScript.isDirty) res = true;
      if (self.autoscaler.isDirty) res = true;
      if (self.networking.isDirty) res = true;
      return res;
    },
    get isValid() {
      let res = true;
      if (self.anyNodePoolNameConflicts) res = false;
      if (self.nodePools.some((n) => !n.isValid)) res = false;
      if (!self.region.isValid) res = false;
      if (!self.network.isValid) res = false;
      // sakeylink
      if (!self.image.isValid) res = false;
      if (!self.isLabelsValid) res = false;
      if (!self.isTagsValid) res = false;
      if (!self.isMetadataValid) res = false;
      if (!self.preInstallScript.isValid) res = false;
      if (!self.autoscaler.isValid) res = false;
      if (!self.networking.isValid) res = false;
      return res;
    },
    get ui_isGcpValid() {
      let res = true;
      if (!self.region.isValid) res = false;
      if (!self.image.isValid) res = false;
      if (!self.saKeyLink) res = false;
      if (!self.projectId.isValid) res = false;
      if (!self.image.isValid) res = false;
      if (!self.network.isValid) res = false;
      return res;
    },
    get dryRun_gcpPathList() {
      return [
        "spec.provider.gcp.saKeyLink",
        "spec.provider.gcp.projectId",
        "spec.provider.gcp.region",
        "spec.provider.gcp.image",
        "spec.provider.gcp.image.recommended",
        "spec.provider.gcp.image.exact",
        "spec.provider.gcp.image.family",
        "spec.provider.gcp.image.family.family",
        "spec.provider.gcp.image.family.project",
        "spec.provider.gcp.network",
      ];
    },
    get ui_isAdvancedValid() {
      let res = true;
      if (!self.isLabelsValid) res = false;
      if (!self.isTagsValid) res = false;
      if (!self.isMetadataValid) res = false;
      if (!self.preInstallScript.isValid) res = false;
      if (!self.autoscaler.isValid) res = false;
      if (!self.networking.isValid) res = false;
      return res;
    },
    get dryRun_advancedPathList() {
      return [
        //
        "spec.provider.gcp.labels",
        ...self.labels.items.map((_, index) => `spec.provider.gcp.labels[${index}]`),
        ...self.labels.items.map((l, index) => `spec.provider.gcp.labels[${index}].${l.firstValue}`),
        "spec.provider.gcp.tags",
        ...self.tags.items.map((_, index) => `spec.provider.gcp.tags[${index}]`),
        "spec.provider.gcp.metadata",
        ...self.metadata.items.map((_, index) => `spec.provider.gcp.metadata[${index}]`),
        ...self.metadata.items.map((l, index) => `spec.provider.gcp.metadata[${index}].${l.firstValue}`),
        "spec.provider.gcp.preInstallScript",
        ...self.autoscaler.dryRun_pathList("gcp"),
        ...self.networking.dryRun_pathList("gcp"),
      ];
    },
    get asObject() {
      let obj: any = {
        projectId: self.projectId.value,
        region: self.region.value,
        labels: self.labelsAsObject,
        tags: self.tagsAsArray,
        metadata: self.metadataAsObject,
        network: self.network.value,
        saKeyLink: self.saKeyLink,
        nodePools: self.nodePools.map((n) => n.asObject),
        networking: self.networking.asObject,
        preInstallScript: self.preInstallScript.value,
        autoscaler: self.autoscaler.asObject,
      };

      if (self.image.type === "recommended") {
        obj.image = { recommended: self.image.recommended };
      }
      if (self.image.type === "exact") {
        obj.image = { exact: self.image.exact };
      }
      if (self.image.type === "family") {
        obj.image = {
          family: {
            project: self.image.familyProject,
            family: self.image.familyFamily,
          },
        };
      }

      return obj;
    },
  }));
export interface Mk8sDraftProviderGCPMobx extends Instance<typeof Mk8sDraftProviderGCPModel> {}
